import { type ComputedRef, computed } from "vue";
import { useStore } from "vuex";
import { DEFAULT_LOGO_URL } from "@/util/branding";

interface HexColorTheme {
  primary: string;
  secondary: string;
  tertiary: string;
}

interface TenantThemeOptions {
  hexColors: ComputedRef<HexColorTheme>;
  primaryColor: ComputedRef<string>;
  secondaryColor: ComputedRef<string>;
  tertiaryColor: ComputedRef<string>;
  isBranded: ComputedRef<boolean>;
  logoUrl: ComputedRef<string>;
  ipedsId: ComputedRef<string>;
  primaryColorClass: ComputedRef<string>;
  primaryColorBorder: ComputedRef<string>;
  primaryColorClassBG: ComputedRef<string>;
  primaryColorClassOutlined: ComputedRef<string>;
  primaryColorClassOutlinedNoHover: ComputedRef<string>;
  secondaryColorClass: ComputedRef<string>;
  secondaryColorClassLinks: ComputedRef<string>;
  secondaryColorClassButton: ComputedRef<string>;
  secondaryColorClassAccent: ComputedRef<string>;
  secondaryColorClassBG: ComputedRef<string>;
  secondaryColorClassOutlined: ComputedRef<string>;
  secondaryColorClassOutlinedNoHover: ComputedRef<string>;
  tertiaryColorClass: ComputedRef<string>;
}

/**
 * Composable that exposes the same theme/branding class names as the global mixin in app.js.
 * Use this in Vue TS components (e.g. script setup) when you need theme values in script
 * or want typed access. In templates you can also use the mixin-injected names directly.
 */
export const useTenantTheming = (): TenantThemeOptions => {
  const store = useStore<{
    brandingDetails?: {
      primary_color?: string;
      secondary_color?: string;
      accent_color?: string;
      logo?: string;
      ipeds_id?: string;
    };
  }>();

  const primaryColor = computed(() => store?.getters?.getBrandingDetails?.primary_color ?? "");
  const secondaryColor = computed(() => store?.getters?.getBrandingDetails?.secondary_color ?? "");
  const tertiaryColor = computed(() => store?.getters?.getBrandingDetails?.accent_color ?? "");

  /**
   * True on a white-label subdomain. `secondary_color` is the flag the branding
   * classes below already switch on, so it is what "branded" means here.
   */
  const isBranded = computed(() => Boolean(secondaryColor.value));

  const logoUrl = computed(() => store?.getters?.getBrandingDetails?.logo || DEFAULT_LOGO_URL);

  /**
   * A tenant saved without an ipeds_id stores the *string* "null": the upsert endpoint
   * defaults it that way (WhiteLabelBrandingController::getAttributes) and classes-service
   * returns it verbatim. Callers use this to look a school up by id, so a truthy
   * four-character placeholder would fetch a school named "null" — normalise it to absent.
   */
  const ipedsId = computed(() => {
    const stored = store?.getters?.getBrandingDetails?.ipeds_id ?? "";
    return stored === "null" || stored === "undefined" ? "" : stored;
  });

  /**
   * For use in style attributes, not tailwinds classes, they will not resolve
   */
  const hexColors = computed<HexColorTheme>(() => {
    return {
      primary: primaryColor.value ? primaryColor.value : "#1E264C",
      secondary: secondaryColor.value ? secondaryColor.value : "#007f80",
      tertiary: tertiaryColor.value ? tertiaryColor.value : "#ffcb00",
    };
  });

  const primaryColorClass = computed(() => (primaryColor.value ? "brandingPrimaryText" : "text-university-primary"));
  const primaryColorBorder = computed(() =>
    primaryColor.value ? "brandingPrimaryBorder" : "border-university-primary",
  );
  const primaryColorClassBG = computed(() => (primaryColor.value ? "brandingPrimary" : "bg-university-primary"));
  const primaryColorClassOutlined = computed(() =>
    primaryColor.value ? "primaryBrandingOutlined" : "text-university-primary border-university-primary",
  );
  const primaryColorClassOutlinedNoHover = computed(() =>
    primaryColor.value ? "primaryBrandingOutlinedNoHover" : "text-university-primary border-university-primary",
  );

  const secondaryColorClass = computed(() =>
    secondaryColor.value ? "brandingSecondaryText" : "text-university-secondary",
  );
  const secondaryColorClassLinks = computed(() =>
    secondaryColor.value ? "brandingSecondaryText" : "text-university-secondary",
  );
  const secondaryColorClassButton = computed(() =>
    secondaryColor.value ? "brandingSecondaryButton" : "bg-university-secondary text-white",
  );
  const secondaryColorClassAccent = computed(() =>
    secondaryColor.value ? "brandingSecondaryAccent" : "nonBrandingAccent",
  );
  const secondaryColorClassBG = computed(() =>
    secondaryColor.value ? "brandingSecondary" : "bg-university-secondary",
  );
  const secondaryColorClassOutlined = computed(() =>
    secondaryColor.value ? "brandingOutlined" : "text-university-secondary border-university-secondary",
  );
  const secondaryColorClassOutlinedNoHover = computed(() =>
    secondaryColor.value ? "brandingOutlinedNoHover" : "text-university-secondary border-university-secondary",
  );

  const tertiaryColorClass = computed(() => (tertiaryColor.value ? "brandingTertiary" : "bg-university-tertiary"));

  return {
    hexColors,
    primaryColor,
    secondaryColor,
    tertiaryColor,
    isBranded,
    logoUrl,
    ipedsId,
    primaryColorClass,
    primaryColorBorder,
    primaryColorClassBG,
    primaryColorClassOutlined,
    primaryColorClassOutlinedNoHover,
    secondaryColorClass,
    secondaryColorClassLinks,
    secondaryColorClassButton,
    secondaryColorClassAccent,
    secondaryColorClassBG,
    secondaryColorClassOutlined,
    secondaryColorClassOutlinedNoHover,
    tertiaryColorClass,
  };
};
